import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { 
  ArrowLeft, Zap, HelpCircle, Calculator, Wallet, FolderOpen, 
  CreditCard, Mail, MessageCircle, ListTodo, Shield, Sparkles,
  ExternalLink, ChevronRight
} from 'lucide-react';

const FeatureCard = ({ icon: Icon, title, description, status, isDark }) => (
  <motion.div
    className={`p-6 rounded-2xl border transition-all duration-300 ${
      isDark 
        ? 'bg-white/5 border-white/10 hover:border-[#81d8d0]/40' 
        : 'bg-white border-gray-200 hover:border-[#81d8d0]/60 shadow-sm'
    }`}
    whileHover={{ y: -2 }}
  >
    <div className="flex items-start gap-4">
      <div className={`p-3 rounded-xl ${isDark ? 'bg-[#81d8d0]/20' : 'bg-[#81d8d0]/10'}`}>
        <Icon size={24} className="text-[#81d8d0]" strokeWidth={1.5} />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{title}</h3>
          {status === 'new' && (
            <span className="px-2 py-0.5 bg-[#81d8d0]/20 text-[#81d8d0] text-xs font-medium rounded-full">Nouveau</span>
          )}
          {status === 'soon' && (
            <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-500 text-xs font-medium rounded-full">Bientôt</span>
          )}
        </div>
        <p className={`text-sm ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>{description}</p>
      </div>
    </div>
  </motion.div>
);

const ResourceLink = ({ title, url, description, isDark }) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    className={`flex items-center justify-between p-4 rounded-xl border transition-all duration-300 group ${
      isDark 
        ? 'bg-white/5 border-white/10 hover:border-[#81d8d0]/40' 
        : 'bg-gray-50 border-gray-200 hover:border-[#81d8d0]/60'
    }`}
  >
    <div>
      <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{title}</h4>
      <p className={`text-sm ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>{description}</p>
    </div>
    <ExternalLink size={18} className={`${isDark ? 'text-[#64748B]' : 'text-gray-400'} group-hover:text-[#81d8d0] transition-colors`} />
  </a>
);

export const DocumentationPage = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const features = [
    {
      icon: Calculator,
      title: "Simulateur d'aides",
      description: "Calcule automatiquement les aides auxquelles tu as droit : APL, RSA, Prime d'activité, bourses CROUS, CSS, Chèque énergie... Basé sur les barèmes 2025.",
      status: 'new'
    },
    {
      icon: MessageCircle,
      title: "Assistant Skipya",
      description: "Ton copilote IA disponible 24/7. Pose-lui n'importe quelle question sur tes impôts, tes aides, tes documents. Il peut aussi créer des tâches automatiquement.",
      status: 'new'
    },
    {
      icon: Mail,
      title: "Connexion Gmail",
      description: "Connecte ton Gmail pour détecter automatiquement tes abonnements depuis tes emails de facturation. Skipya identifie les doublons et te propose des économies.",
      status: 'new'
    },
    {
      icon: Wallet,
      title: "Gestion des finances",
      description: "Suis tes entrées et sorties d'argent. Import CSV disponible. Visualise ton solde en temps réel.",
      status: null
    },
    {
      icon: CreditCard,
      title: "Suivi des abonnements",
      description: "Garde une vue sur tous tes abonnements (streaming, télécom, sport...). Détecte les doublons et les dépenses inutiles.",
      status: null
    },
    {
      icon: ListTodo,
      title: "Gestion des tâches",
      description: "Crée et suis tes tâches administratives. Skipya peut créer automatiquement des tâches pour tes démarches d'aides.",
      status: null
    },
    {
      icon: FolderOpen,
      title: "Coffre-fort documents",
      description: "Stocke tes documents importants en sécurité : fiches de paie, avis d'imposition, attestations...",
      status: 'soon'
    },
    {
      icon: Shield,
      title: "Génération de lettres",
      description: "Génère automatiquement des courriers administratifs : résiliation, réclamation, demande d'aide. Bientôt avec envoi automatique !",
      status: 'soon'
    }
  ];

  const resources = [
    { title: "CAF - Aides au logement", url: "https://www.caf.fr", description: "APL, ALF, ALS" },
    { title: "Service-public.fr", url: "https://www.service-public.fr", description: "Toutes les démarches admin" },
    { title: "impots.gouv.fr", url: "https://www.impots.gouv.fr", description: "Déclaration et paiement" },
    { title: "messervices.etudiant.gouv.fr", url: "https://www.messervices.etudiant.gouv.fr", description: "DSE, bourses CROUS" },
    { title: "France Travail", url: "https://www.francetravail.fr", description: "Emploi, allocations chômage" },
    { title: "Ameli.fr", url: "https://www.ameli.fr", description: "Assurance maladie, CSS" }
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-[#0F0F1A]' : 'bg-[#FAFBFC]'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-40 backdrop-blur-xl border-b transition-colors duration-300 ${
        isDark ? 'bg-[#0F0F1A]/80 border-white/5' : 'bg-white/80 border-gray-200'
      }`}>
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className={`p-2 rounded-xl transition-colors ${
              isDark ? 'hover:bg-white/5 text-[#94A3B8]' : 'hover:bg-gray-100 text-gray-600'
            }`}
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#81d8d0] to-[#4ECDC4] flex items-center justify-center">
              <Zap size={20} className="text-[#0F0F1A]" />
            </div>
            <div>
              <h1 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Documentation Skipya
              </h1>
              <p className={`text-sm ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>
                Guide complet de l'application
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 pb-24">
        {/* Introduction */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className={`p-8 rounded-3xl ${isDark ? 'bg-gradient-to-br from-[#81d8d0]/20 to-transparent border border-[#81d8d0]/20' : 'bg-gradient-to-br from-[#81d8d0]/10 to-white border border-[#81d8d0]/20'}`}>
            <div className="flex items-start gap-4">
              <Sparkles size={32} className="text-[#81d8d0] flex-shrink-0 mt-1" />
              <div>
                <h2 className={`text-2xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Bienvenue sur Skipya !
                </h2>
                <p className={`text-lg leading-relaxed ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>
                  Skipya est ton copilote administratif intelligent. Notre mission : t'aider à <span className="text-[#81d8d0] font-medium">récupérer l'argent qui t'appartient</span> et à <span className="text-[#81d8d0] font-medium">simplifier ta vie administrative</span>.
                </p>
                <div className={`mt-4 p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-[#81d8d0]/10'}`}>
                  <p className={`text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}>
                    💡 <strong>Le savais-tu ?</strong> De nombreuses aides restent oubliées chaque année par les jeunes français. Skipya t'aide à repérer celles auxquelles tu pourrais avoir droit selon ta situation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Features */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-12"
        >
          <h2 className={`text-xl font-semibold mb-6 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <HelpCircle size={24} className="text-[#81d8d0]" />
            Fonctionnalités
          </h2>
          <div className="grid gap-4">
            {features.map((feature, index) => (
              <FeatureCard key={index} {...feature} isDark={isDark} />
            ))}
          </div>
        </motion.section>

        {/* How to use simulator */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-12"
        >
          <h2 className={`text-xl font-semibold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            🎯 Comment utiliser le simulateur d'aides ?
          </h2>
          <div className={`p-6 rounded-2xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
            <ol className="space-y-4">
              {[
                "Rends-toi sur la page \"Aides\" depuis le dashboard",
                "Remplis le questionnaire avec tes infos (âge, situation, revenus, loyer...)",
                "Clique sur \"Calculer mes aides\"",
                "Découvre toutes les aides auxquelles tu as droit !",
                "Clique sur \"Créer les tâches\" pour avoir un suivi de tes démarches"
              ].map((step, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="w-7 h-7 rounded-full bg-[#81d8d0]/20 text-[#81d8d0] flex items-center justify-center text-sm font-semibold flex-shrink-0">
                    {index + 1}
                  </span>
                  <span className={isDark ? 'text-[#94A3B8]' : 'text-gray-600'}>{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </motion.section>

        {/* Resources */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-12"
        >
          <h2 className={`text-xl font-semibold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            🔗 Ressources utiles
          </h2>
          <div className="grid gap-3">
            {resources.map((resource, index) => (
              <ResourceLink key={index} {...resource} isDark={isDark} />
            ))}
          </div>
        </motion.section>

        {/* Contact */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className={`p-6 rounded-2xl text-center ${isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-50 border border-gray-200'}`}>
            <p className={`text-sm ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>
              Une question ? Un bug ? Parle à l'assistant Skipya, il est là pour t'aider ! 🤖
            </p>
          </div>
        </motion.section>
      </main>
    </div>
  );
};

export default DocumentationPage;
